import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Button from '@/components/primitives/Button.jsx';
import Skeleton from '@/components/primitives/Skeleton.jsx';
import Textarea from '@/components/primitives/Textarea.jsx';
import BackLink from '@/components/patterns/BackLink.jsx';
import Dialog from '@/components/patterns/Dialog.jsx';
import pickFreeSlug from '@/lib/knowledge/pickFreeSlug.js';
import splitTitledDocument from '@/lib/knowledge/splitTitledDocument.js';
import toKnowledgeSlug from '@/lib/knowledge/toKnowledgeSlug.js';
import toastManager from '@/lib/toast/toastManager.js';
import { getKnowledgeFiles, putKnowledgeFile, removeKnowledgeFile } from '@/redux/requests/knowledgeFilesRequests.js';
import { getKnowledgeByPath } from '@/redux/requests/skillsRequests.js';
import { knowledgeFiles } from '@/redux/selectors/pieveSelectors/knowledgeFileSelectors.js';

const ROOT = 'organization/';

/**
 * Writes or edits one organization note: a title and the text. A new note is stored under a file
 * name made from its title; the title is kept as the note's first line, so Pieve reads it too.
 */
const KnowledgeDocumentPage = ({ documentName }) => {
  const navigate = useNavigate();
  const files = useSelector(knowledgeFiles);
  const isNew = documentName === 'new';
  const [draft, setDraft] = useState(isNew ? { title: '', body: '', path: null, nodeId: null } : null);
  const [loadError, setLoadError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (isNew) {
      return;
    }

    const path = `${ROOT}${documentName}`;
    getKnowledgeByPath({ path }).then((res) => {
      const row = Array.isArray(res) ? res[0] : null;

      if (!row) {
        setLoadError(res?.message === 'Not found.' ? 'This document no longer exists.' : res?.message || 'This document could not be loaded.');
        return;
      }

      setDraft({ ...splitTitledDocument(row.content), path, nodeId: row.id });
    });
  }, [isNew, documentName]);

  const update = (key) => (event) => setDraft((current) => ({ ...current, [key]: event.target.value }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaveError('');
    setIsSaving(true);
    const takenSlugs = files.map((file) => file.path.slice(ROOT.length).replace(/\.md$/, ''));
    const path = draft.path || `${ROOT}${pickFreeSlug({ slug: toKnowledgeSlug(draft.title), takenSlugs })}.md`;
    const content = `# ${draft.title.replace(/\s+/g, ' ').trim()}\n\n${draft.body.trim()}\n`;
    const res = await putKnowledgeFile({ path, content });
    setIsSaving(false);

    if (res?.success === false) {
      setSaveError(res.message || 'The document could not be saved.');
      return;
    }

    await getKnowledgeFiles({ root: 'organization' });
    toastManager.add({ type: 'success', title: isNew ? 'Document added' : 'Document saved', description: 'Pieve uses it from your next conversation.' });

    if (isNew) {
      navigate(`/knowledge/${encodeURIComponent(path.slice(ROOT.length))}`, { replace: true });
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    const res = await removeKnowledgeFile({ nodeId: draft.nodeId, path: draft.path });
    setIsDeleting(false);

    if (res?.success === false) {
      setSaveError(res.message || 'The document could not be deleted.');
      setIsConfirmingDelete(false);
      return;
    }

    toastManager.add({ type: 'success', title: 'Document deleted' });
    navigate('/knowledge');
  };

  return (
    <div className="comp-page knowledge-editor">
      <BackLink to="/knowledge">Knowledge</BackLink>
      {loadError ? (
        <p className="knowledge-error">{loadError}</p>
      ) : !draft ? (
        <Skeleton shape="block" height={320} />
      ) : (
        <form className="knowledge-editor__form" onSubmit={handleSubmit}>
          <input className="knowledge-editor__title" aria-label="Title" placeholder="Untitled document" value={draft.title} onChange={update('title')} required />
          <Textarea label="Text" hint="Plain words or Markdown. Keep one subject per document so Pieve finds the right one." rows={16} value={draft.body} onChange={update('body')} />
          {saveError && (
            <p className="knowledge-error" role="alert">
              {saveError}
            </p>
          )}
          <div className="knowledge-editor__actions">
            <Button type="submit" variant="primary" loading={isSaving} disabled={!draft.title.trim() || !draft.body.trim()}>
              {isNew ? 'Add document' : 'Save document'}
            </Button>
            {!isNew && (
              <Button variant="ghost" onClick={() => setIsConfirmingDelete(true)}>
                Delete
              </Button>
            )}
          </div>
        </form>
      )}
      <Dialog
        open={isConfirmingDelete}
        onOpenChange={setIsConfirmingDelete}
        title={`Delete ${draft?.title || 'this document'}?`}
        description="Pieve stops using it from your next conversation."
        footer={
          <>
            <Button variant="ghost" onClick={() => setIsConfirmingDelete(false)}>
              Cancel
            </Button>
            <Button variant="danger" loading={isDeleting} onClick={handleDelete}>
              Delete document
            </Button>
          </>
        }
      />
    </div>
  );
};

export default KnowledgeDocumentPage;
